import { useEffect, useState } from "react";
import { ArrowUpRight, Linkedin, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import api from "../../lib/api";
import { initials, IMAGES, mediaUrl } from "./siteData";
import { useSeo } from "./useSeo";
import { useEnquiry } from "./enquiryContext";
import PageHero from "./PageHero";

function Portrait({ m, className = "" }) {
  if (m.photo) return <img src={mediaUrl(m.photo)} alt={m.name} className={`object-cover object-top bg-slate-200 ${className}`} loading="lazy" data-testid={`team-photo-${m.id}`} />;
  return <div className={`bg-[#14263D] text-[#C4A66A] font-serif text-5xl flex items-center justify-center ${className}`}>{initials(m.name)}</div>;
}

function LinkedInLink({ m, testid }) {
  if (!m.linkedin) return null;
  const href = m.linkedin.startsWith("http") ? m.linkedin : `https://${m.linkedin}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`${m.name} on LinkedIn`}
      className="h-9 w-9 inline-flex items-center justify-center border border-slate-200 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors" data-testid={testid}>
      <Linkedin className="h-4 w-4" />
    </a>
  );
}

function TeamGroup({ id, eyebrow, title, members, onOpen }) {
  if (!members.length) return null;
  const cols = id === "partners" ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 max-w-4xl mx-auto";
  return (
    <div className="mb-20 last:mb-0" data-testid={`team-group-${id}`}>
      <div className="site-eyebrow">{eyebrow}</div>
      <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#14263D] mt-3 mb-10">{title}</h2>
      <div className={`grid ${cols} gap-8 site-reveal`} data-testid={id === "partners" ? "team-grid" : "team-grid-professionals"}>
        {members.map((m) => <MemberCard key={m.id} m={m} onOpen={() => onOpen(m)} />)}
      </div>
    </div>
  );
}

function MemberCard({ m, onOpen }) {
  return (
    <div className="site-card-lift bg-white border border-slate-200 flex flex-col group" data-testid={`team-card-${m.id}`}>
      <button onClick={onOpen} className="relative overflow-hidden text-left" data-testid={`team-open-${m.id}`}>
        <Portrait m={m} className="w-full aspect-[4/5] transition-transform duration-700 group-hover:scale-[1.03]" />
        <span className="absolute inset-x-0 bottom-0 h-1 bg-[#C4A66A] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
      </button>
      <div className="p-7 flex flex-col flex-1">
        {m.title && <div className="text-[11px] uppercase tracking-[0.22em] text-[#C4A66A]">{m.title}</div>}
        <h3 className={`font-serif text-2xl font-semibold text-[#14263D] ${m.title ? "mt-2" : ""}`}>{m.name}</h3>
        {m.practice && <div className="text-sm text-slate-600 mt-2">{m.practice}</div>}
        {m.experience && <div className="text-xs text-slate-500 mt-3">{m.experience}</div>}
        <div className="mt-auto pt-6 flex items-center justify-between">
          <button onClick={onOpen} className="text-[11px] uppercase tracking-[0.25em] text-[#14263D] inline-flex items-center gap-2 site-link" data-testid={`team-profile-btn-${m.id}`}>
            View profile <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
          <LinkedInLink m={m} testid={`team-linkedin-${m.id}`} />
        </div>
      </div>
    </div>
  );
}

function BioModal({ sel, onClose }) {
  return (
    <Dialog open={!!sel} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="site max-w-3xl max-h-[90vh] overflow-y-auto rounded-none border-t-4 border-t-[#C4A66A] p-0" data-testid="team-bio-modal">
        {sel && (
          <div className="grid md:grid-cols-5">
            <Portrait m={sel} className="md:col-span-2 w-full h-72 md:h-full min-h-[280px]" />
            <div className="md:col-span-3 p-8">
              <DialogHeader className="text-left">
                {sel.title && <div className="text-[11px] uppercase tracking-[0.22em] text-[#C4A66A]">{sel.title}</div>}
                <DialogTitle className="font-serif text-3xl text-[#14263D]">{sel.name}</DialogTitle>
                <DialogDescription>{sel.qualifications || sel.practice}</DialogDescription>
              </DialogHeader>
              {sel.experience && <div className="mt-4 text-sm font-medium text-[#14263D]">{sel.experience}</div>}
              <p className="text-slate-700 leading-relaxed whitespace-pre-line mt-5 text-[15px]" data-testid="team-bio-text">{sel.bio}</p>
              <div className="flex flex-wrap items-center gap-4 mt-6 text-sm">
                {sel.email && <a href={`mailto:${sel.email}`} className="inline-flex items-center gap-2 text-[#14263D] site-link" data-testid="team-bio-email"><Mail className="h-4 w-4" />{sel.email}</a>}
                {sel.linkedin && <a href={sel.linkedin.startsWith("http") ? sel.linkedin : `https://${sel.linkedin}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#0A66C2] site-link" data-testid="team-bio-linkedin"><Linkedin className="h-4 w-4" />LinkedIn profile</a>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  useSeo("Partners & Team", "Meet the partners and professionals of VCAJ & Associates LLP, Chartered Accountants.");
  const [team, setTeam] = useState(null);
  const [sel, setSel] = useState(null);
  const openEnquiry = useEnquiry();
  useEffect(() => { api.get("/public/site/team").then((r) => setTeam(r.data)).catch(() => setTeam([])); }, []);

  return (
    <div data-testid="team-page">
      <PageHero testid="team-hero" image={IMAGES.team} eyebrow="Team" title="The people who sign off."
        intro="Partners with three decades of combined Big Four experience, who lead every engagement and stay reachable throughout." />
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {team === null ? (
            <div className="text-sm text-slate-500" data-testid="team-loading">Loading…</div>
          ) : team.length === 0 ? (
            <div className="text-slate-500" data-testid="team-empty">Team profiles will be published soon.</div>
          ) : (
            <>
              <TeamGroup id="partners" eyebrow="Partners" title="Leadership." members={team.filter((m) => (m.group || "partner") === "partner")} onOpen={setSel} />
              <TeamGroup id="professionals" eyebrow="Our professionals" title="The team behind every engagement." members={team.filter((m) => m.group === "team")} onOpen={setSel} />
            </>
          )}
        </div>
      </section>
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#14263D] max-w-xl">Want to speak to a partner directly?</h2>
          <button onClick={() => openEnquiry()} className="btn-site-navy h-12 px-8 text-sm font-semibold inline-flex items-center gap-2 w-fit" data-testid="team-enquire-btn">Request a call <ArrowUpRight className="h-4 w-4" /></button>
        </div>
      </section>
      <BioModal sel={sel} onClose={() => setSel(null)} />
    </div>
  );
}
